import type { FieldDefinition, IssuedCredential, PersonDetail } from '@sentra/contracts';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FailureNote } from '@/api/failure';
import { useApiMutation } from '@/api/mutations';
import { usePersonCredentials } from '@/api/queries';
import { apiRoutes } from '@/api/routes';
import { useIsPreprinted, useOffersEmergencyPass } from '@/app/providers/config';
import { Button } from '@/design/components/button';
import { BandField, useBandEntry } from '@/features/persons/form/band-field';
import { PersonForm } from '@/features/persons/form/person-form';
import { PersonImagesPanel } from '@/features/persons/images/panel';
import { firstNameOf } from '@/features/persons/person-name';
import { PassDelivery } from '@/features/scan/reveal';
import { ConfirmDialog } from '@/features/typed-confirm';
import { useFormatters } from '@/i18n/use-format';

/**
 * Everything that changes one person, on one screen.
 *
 * **The record is a view and this is the other half of it.** The stored fields, the photographs,
 * the pass and the situation were all editable from the record itself, which put an upload, a band
 * binding that supersedes somebody's working wristband, and a status select that wrote on change,
 * onto the screen an operator has open while a person stands at the desk. They are all here now,
 * and the record shows the same things without a control on any of them.
 *
 * **Three writes, and they are deliberately not one.** The fields and the situation travel together
 * in the form's own PATCH, because the contract already carries `status` and one request means one
 * version and one revision. The photographs and the pass do not: they are different routes with
 * different lifetimes, and neither bumps `persons.version` — `routes/persons/images/ingest.ts` and
 * `credentials/service.ts` take the person's row lock and nothing more — so a photograph added
 * while the form is open cannot make the operator's Save conflict with their own act.
 *
 * **The issued pass is the caller's, not this screen's.** A code is in this browser for exactly one
 * moment and can never be fetched back by an operator, since revealing a stored credential is
 * `credential:reveal`. Holding it here would lose it the instant the form saved and this component
 * unmounted, so it is lifted into the register's screen state and handed back down.
 */
export function AmendScreen({
  person,
  definitions,
  method,
  issued,
  onIssued,
  onAmended,
  onCancel,
}: Readonly<{
  person: PersonDetail;
  definitions: readonly FieldDefinition[];
  method: Parameters<typeof PersonForm>[0]['method'];
  /** A pass this session has issued, which only the caller can keep across the two screens. */
  issued: IssuedCredential | undefined;
  onIssued: (issued: IssuedCredential) => void;
  onAmended: (person: PersonDetail) => void;
  onCancel: () => void;
}>): ReactNode {
  const { t } = useTranslation();
  const format = useFormatters();

  const { credentials } = usePersonCredentials(person.id);
  const [replacing, setReplacing] = useState(false);
  /** Whether the button has been pressed with nothing usable in the band field. */
  const [attempted, setAttempted] = useState(false);
  const preprinted = useIsPreprinted();
  // Only the minting answer is offered HERE, and never the deferring one: this screen exists to
  // bind a band, so "there is no band yet" is not a thing to declare on it — the operator simply
  // does not press the button. `POST /api/v1/credentials` refuses a bandless request in that mode
  // anyway, so offering the box would be offering a refusal.
  const offerEmergency = useOffersEmergencyPass();
  // The same entry the registration form uses, so one rule decides what a usable band is on both
  // screens. Inactive in `on_demand`, where there is no band to hand and the server mints.
  const band = useBandEntry(preprinted);

  const reissue = useApiMutation(
    apiRoutes.credentialIssue,
    (passCode: string | undefined) => ({
      body: {
        personId: person.id,
        type: 'qr' as const,
        supersedeExisting: true,
        // **The band is carried IN THE INPUT rather than read at submit time**, which is what makes
        // the idempotency key right: `useApiMutation` keys on the input's signature, so pressing the
        // button twice with one code retries under one key while a corrected code mints a new one.
        // A reissue is the half AD-30 cannot protect with a uniqueness constraint, because it
        // retires the existing row before it inserts.
        ...(passCode === undefined ? {} : { passCode }),
      },
    }),
    {
      onSuccess: (credential) => {
        onIssued(credential);
      },
    },
  );

  /**
   * Whether a pass can be issued from here, and which of the two acts it would be.
   *
   * **Both need a LOADED list**: `find` over an absent list answers `undefined` exactly as a list
   * with no active pass does, so a control gated on that alone appears while the read is still in
   * flight and offers the wrong one of the two.
   *
   * **An ACTIVE pass does not hide the control**, because the request carries `supersedeExisting`
   * and is therefore a REPLACEMENT: the old credential is retired in the same transaction and, in a
   * pre-printed installation, a second blank is claimed — which is exactly what a lost or broken
   * wristband needs. Hidden, there was no way to replace one at all, because nothing in this
   * application revokes a credential either.
   */
  const passes = credentials;
  const activePass = passes?.some((credential) => credential.status === 'active') === true;
  const offerReissue = passes !== undefined;

  return (
    <div className="flex flex-col gap-6" data-testid="person-amend">
      <PersonForm
        mode={{ kind: 'amend', person }}
        definitions={definitions}
        method={method}
        onAmended={onAmended}
        onCancel={onCancel}
      />

      <PersonImagesPanel personId={person.id} />

      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">{t('person.record.credentials')}</h3>
        {(passes ?? []).length === 0 && (
          <p className="text-sm text-muted-foreground">{t('person.record.noCredential')}</p>
        )}
        <ul className="flex flex-col gap-1">
          {(passes ?? []).map((credential) => (
            <li
              key={credential.id}
              className="flex flex-wrap gap-3 text-sm"
              data-testid="credential-row"
            >
              <span>{t(`person.record.credential_${credential.status}`)}</span>
              <span data-numeric className="text-muted-foreground">
                {format.dateTime(credential.issuedAt)}
              </span>
            </li>
          ))}
        </ul>
        {offerReissue && (
          <>
            {/*
              The band, in a pre-printed installation, because a pass here comes off a wristband in
              the operator's hand exactly as it does at registration. The same control and the same
              readiness test as the registration form, so the two screens cannot come to disagree
              about what a usable band is. In `on_demand` the field is absent and the server mints.
            */}
            {preprinted && (
              <BandField
                entry={band}
                required={attempted}
                offerNoBand={offerEmergency}
                noBandLabel={t('person.form.emergencyLabel')}
                noBandHint={t('person.form.emergencyHint')}
              />
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start"
              disabled={reissue.submitting}
              data-testid="reissue-credential"
              onClick={() => {
                setAttempted(true);
                if (!band.ready) {
                  return;
                }
                if (activePass) {
                  setReplacing(true);
                  return;
                }
                reissue.submit(band.passCode);
              }}
            >
              {t(activePass ? 'person.record.replaceBand' : 'person.record.issueBand')}
            </Button>
          </>
        )}
        {reissue.error != null && <FailureNote error={reissue.error} />}
        {issued !== undefined && (
          <PassDelivery
            pass={{ kind: 'issued', issued, shortRef: person.shortRef }}
            personName={firstNameOf(person)}
          />
        )}
        {/*
          Replacing a WORKING pass is confirmed, and issuing a first one is not. The old credential
          stops resolving at every door the moment this commits, so an operator who meant to look at
          the record and pressed the wrong control would take somebody's working wristband away
          without being asked. A person holding none has nothing to lose by the press.
        */}
        <ConfirmDialog
          open={replacing}
          title={t('person.record.replaceTitle')}
          body={t('person.record.replaceBody')}
          confirmLabel={t('person.record.replaceBand')}
          pending={reissue.submitting}
          onConfirm={() => {
            setReplacing(false);
            reissue.submit(band.passCode);
          }}
          onCancel={() => {
            setReplacing(false);
          }}
        />
      </section>
    </div>
  );
}
